import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect } from "react";
import { IoClose, IoSearch } from "react-icons/io5";
import axios from "axios";
import { Pagination } from "../../components/pagination";

const TrainingParticipantsModal = ({ isOpen, onClose }) => {
  const [participantsData, setParticipantsData] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchParticipants = async (page = 1, search = "") => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/admin/all-training-participants`,
        {
          params: { page, search },
        }
      );
      const data = response.data;

      setParticipantsData(data.data);
      setPagination({
        current_page: data.current_page,
        last_page: data.last_page,
        total: data.total,
      });
    } catch (error) {
      console.error("Error fetching participants:", error);
      alert("Failed to fetch participants. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleUserStatus = async (participantId, currentStatus) => {
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      const newStatus =
        currentStatus === "Not Started" ? "blocked" : "not started";
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/admin/toggle-user-status/${participantId}`,
        {
          status: newStatus,
        }
      );

      if (response.status === 200) {
        setParticipantsData((prev) =>
          prev.map((participant) =>
            participant.id === participantId
              ? { ...participant, trainingStatus: newStatus }
              : participant
          )
        );
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      console.error("Error updating user status:", error);
      alert("Failed to update user status. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchParticipants();
    }
  }, [isOpen]);

  const handlePageChange = (page) => {
    fetchParticipants(page, searchQuery);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchParticipants(1, searchQuery);
  };

  const renderArrayAsList = (array) => {
    if (!array || array.length === 0)
      return <span className="text-gray-400">None</span>;
    return (
      <ul className="list-disc pl-5">
        {array.map((item, index) => (
          <li key={index} className="py-1">
            {item}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Training Participants
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <IoClose size={24} />
                  </button>
                </div>

                <form onSubmit={handleSearch} className="mb-4">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <IoSearch className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="block w-full pl-10 sm:pr-3 pr-[4.3rem] py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Search participants..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button
                      type="submit"
                      className="absolute right-0 top-0 h-full px-4 text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      Search
                    </button>
                  </div>
                </form>

                <div className="mt-2">
                  {isLoading ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Participant Name
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Assigned Training
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Current Training
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Completed Trainings
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Days Left
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {participantsData.length > 0 ? (
                              participantsData.map((participant) => (
                                <tr key={participant.id}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {participant.participantName}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900">
                                    {renderArrayAsList(
                                      participant.assignedTraining
                                    )}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900">
                                    {renderArrayAsList(
                                      participant.currentTraining
                                    )}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900">
                                    {renderArrayAsList(
                                      participant.completedTrainings
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {participant.daysLeft}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <div className="flex items-center">
                                      <label className="inline-flex items-center cursor-pointer">
                                        <input
                                          type="checkbox"
                                          className="sr-only peer"
                                          checked={
                                            participant.trainingStatus ===
                                            "blocked"
                                          }
                                          onChange={() =>
                                            toggleUserStatus(
                                              participant.id,
                                              participant.trainingStatus
                                            )
                                          }
                                          disabled={isUpdating}
                                        />
                                        <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                        <span className="ml-2 text-sm font-medium text-gray-900">
                                          {[
                                            "in-progress",
                                            "Not Started",
                                            "not-started",
                                          ].includes(participant.trainingStatus)
                                            ? "Enabled"
                                            : "Blocked"}
                                        </span>
                                      </label>
                                      {isUpdating &&
                                        participant.id ===
                                          participantsData.find(
                                            (p) => p.id === participant.id
                                          ).id && (
                                          <div className="ml-2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                        )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan="6"
                                  className="px-6 py-4 text-center text-sm text-gray-500"
                                >
                                  No participants found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {participantsData.length > 0 && (
                        <div className="mt-4 flex justify-between items-center">
                          <div className="text-sm text-gray-700">
                            Showing page {pagination.current_page} of{" "}
                            {pagination.last_page}
                          </div>
                          <Pagination
                            total={pagination.last_page}
                            current={pagination.current_page}
                            func={handlePageChange}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TrainingParticipantsModal;
